// =====================================================================
// PrelevementFormModal.jsx - Modal create/edit prelevement (M-001 Faza 2)
// =====================================================================
// Pattern: PartialAttModal.jsx ca referinta de stil.
// Props:
//   - drawId: null = create, string = edit existing
//   - onClose: () => void
//   - onSaved: () => void
//
// Permission logic:
//   - create.own / create.all -> POST nou
//   - update.own + same-day OR update.all -> edit
//   - delete.all -> buton Supprimer vizibil in mod edit
//   - Daca nu poate edita, modal in read-only.
// =====================================================================

import { useState, useEffect } from 'react';
import {
  supplyDraws as drawsApi,
  supplyTypes as typesApi,
  projects as projectsApi,
} from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import SupplyTypeQuickCreate from './SupplyTypeQuickCreate.jsx';

const today = () => new Date().toISOString().slice(0, 10);
// Stockage sur dalle (22-10-2026) : type réservé aux lignes écrites par le calcul (une par commande et par mois) —
// jamais proposé pour une saisie, ses lignes s'ouvrent en lecture seule (le serveur refuse aussi).
const CODE_STOCKAGE = 'STOCKAGE_DALLE';

export default function PrelevementFormModal({ drawId, onClose, onSaved }) {
  const { user, can } = useAuth();
  const isEdit = !!drawId;

  const [date, setDate]             = useState(today());
  const [projectId, setProjectId]   = useState('');
  const [supplyTypeId, setTypeId]   = useState('');
  const [quantity, setQuantity]     = useState(1);
  const [comment, setComment]       = useState('');

  const [ref, setRef]                 = useState('');
  const [createdByName, setCreator]   = useState(user?.name || '');
  const [invoiced, setInvoiced]       = useState(false);
  const [createdAt, setCreatedAt]     = useState(null);
  const [updatedAt, setUpdatedAt]     = useState(null);

  const [projects, setProjects]      = useState([]);
  const [supplyTypes, setSupplyTypes] = useState([]);

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [automatique, setAutomatique] = useState(false);   // ligne du stockage sur dalle

  const [quickCreateOpen, setQuickCreateOpen] = useState(false);

  const canUpdateAll = can('supply_draws.update.all');
  const canUpdateOwn = can('supply_draws.update.own');
  const canDelete    = can('supply_draws.delete.all');
  const canManageCatalog = can('supply_draws.manage_catalog.all');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [projList, typeList] = await Promise.all([
          projectsApi.list({ isActive: true }),
          typesApi.list({ isActive: true }),
        ]);
        if (cancelled) return;
        setProjects(Array.isArray(projList) ? projList : (projList.items || []));
        setSupplyTypes(Array.isArray(typeList) ? typeList : (typeList.items || []));

        if (isEdit) {
          const draw = await drawsApi.get(drawId);
          if (cancelled) return;
          setRef(draw.ref || '');
          setDate(draw.date ? draw.date.slice(0, 10) : today());
          setProjectId(draw.projectId || '');
          setTypeId(draw.supplyTypeId || '');
          setQuantity(draw.quantity ?? 1);
          setComment(draw.comment || '');
          setCreator(draw.createdBy?.name || '');
          setInvoiced(!!draw.invoiced);
          setCreatedAt(draw.createdAt || null);
          setUpdatedAt(draw.updatedAt || null);

          const isOwn = draw.createdByUserId === user?.id;
          const isSameDay = draw.date?.slice(0,10) === today();
          const canEdit = canUpdateAll || (isOwn && isSameDay && canUpdateOwn);
          const auto = draw.supplyType?.code === CODE_STOCKAGE;
          setAutomatique(auto);
          setReadOnly(!canEdit || auto);
        } else {
          try {
            const { ref: nextRef } = await drawsApi.nextRef();
            if (!cancelled) setRef(nextRef);
          } catch (_e) { /* nu blochez form */ }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[PrelevementFormModal] load error', err);
          setError(err.response?.data?.message || 'Erreur lors du chargement');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [drawId, isEdit, user?.id, canUpdateAll, canUpdateOwn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    if (!projectId || !supplyTypeId || !quantity || Number(quantity) <= 0) {
      setError('Veuillez remplir tous les champs obligatoires (projet, type, quantité > 0).');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        date,
        projectId,
        supplyTypeId,
        quantity: Number(quantity),
        comment: comment.trim() || undefined,
      };
      if (isEdit) {
        await drawsApi.update(drawId, payload);
      } else {
        await drawsApi.create(payload);
      }
      onSaved();
    } catch (err) {
      console.error('[PrelevementFormModal] save error', err);
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete || !isEdit) return;
    if (!confirm(`Supprimer le prélèvement ${ref} ?\n\nCette action est irréversible.`)) return;
    setSaving(true);
    setError(null);
    try {
      await drawsApi.remove(drawId);
      onSaved();
    } catch (err) {
      console.error('[PrelevementFormModal] delete error', err);
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
      setSaving(false);
    }
  };

  const handleTypeCreated = (newType) => {
    setSupplyTypes(prev => [...prev, newType]);
    setTypeId(newType.id);
    setQuickCreateOpen(false);
  };

  if (loading) {
    return (
      <div style={backdropStyle} onClick={onClose}>
        <div onClick={e => e.stopPropagation()} style={{ ...formStyle, padding: 40, textAlign: 'center' }}>
          Chargement...
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={backdropStyle} onClick={onClose}>
        <form className="modal-mobile-fullscreen" onClick={e => e.stopPropagation()} onSubmit={handleSubmit} style={formStyle}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 20px', borderBottom: '1px solid #e2e8f0',
          }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 500 }}>
              {isEdit ? (readOnly ? 'Détails du prélèvement' : 'Modifier le prélèvement') : 'Nouveau prélèvement'}
            </h2>
            <div style={{
              fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12,
              color: '#64748b', padding: '2px 8px',
              background: '#f1f5f9', borderRadius: 3,
            }}>
              {ref || '(auto)'}
            </div>
          </div>

          {automatique ? (
            <div style={{
              padding: '8px 20px', background: '#e0f2fe', color: '#075985',
              fontSize: 12, borderBottom: '1px solid #7dd3fc',
            }}>
              Ligne calculée automatiquement (stockage sur dalle, Commandes prêtes) : elle se met à jour seule jusqu'à la
              clôture du mois. Détail par commande dans Facturation. Elle se marque « facturé » dans la liste.
            </div>
          ) : readOnly && (
            <div style={{
              padding: '8px 20px', background: '#fef3c7', color: '#78350f',
              fontSize: 12, borderBottom: '1px solid #fbbf24',
            }}>
              Lecture seule — vous n'avez pas la permission de modifier ce prélèvement.
            </div>
          )}

          <div style={{ padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <label style={labelStyle}>
              <span style={labelTextStyle}>Projet *</span>
              <select value={projectId} onChange={e => setProjectId(e.target.value)}
                      disabled={readOnly || saving} required style={inputStyle}>
                <option value="">— Sélectionner —</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </label>

            <label style={labelStyle}>
              <span style={labelTextStyle}>Date *</span>
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                     disabled={readOnly || saving} required style={inputStyle} />
            </label>

            <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
              <span style={labelTextStyle}>Type de fourniture *</span>
              <select value={supplyTypeId} onChange={e => setTypeId(e.target.value)}
                      disabled={readOnly || saving} required style={inputStyle}>
                <option value="">— Sélectionner —</option>
                {supplyTypes.filter(t => t.code !== CODE_STOCKAGE || t.id === supplyTypeId).map(t => (
                  <option key={t.id} value={t.id}>
                    {t.code} — {t.label} ({t.unit})
                  </option>
                ))}
              </select>
              {!readOnly && canManageCatalog && (
                <button type="button" onClick={() => setQuickCreateOpen(true)}
                        style={{
                          marginTop: 4, padding: 0, background: 'transparent', border: 'none',
                          color: '#2563eb', fontSize: 12, cursor: 'pointer', textAlign: 'left',
                        }}>
                  + Nouveau type de fourniture
                </button>
              )}
            </label>

            <label style={labelStyle}>
              <span style={labelTextStyle}>Quantité *</span>
              <input type="number" min={0.01} step="any" value={quantity}
                     onChange={e => setQuantity(e.target.value)}
                     disabled={readOnly || saving} required style={inputStyle} />
            </label>

            <label style={labelStyle}>
              <span style={labelTextStyle}>Créé par</span>
              <input type="text" value={createdByName} readOnly
                     style={{ ...inputStyle, opacity: 0.7, background: '#f8fafc' }} />
            </label>

            <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
              <span style={labelTextStyle}>Commentaire (optionnel)</span>
              <textarea value={comment} onChange={e => setComment(e.target.value)}
                        disabled={readOnly || saving} rows={3}
                        style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
            </label>

            {isEdit && (
              <div style={{
                gridColumn: '1 / -1', padding: 10,
                background: '#f8fafc', border: '1px solid #e2e8f0',
                borderRadius: 4, fontSize: 11, color: '#64748b',
              }}>
                <strong>Statut facturation:</strong> {invoiced ? '✓ Facturé' : 'Non facturé'} &nbsp;|&nbsp;
                <strong>Créé:</strong> {createdAt ? new Date(createdAt).toLocaleString('fr-BE') : '—'} &nbsp;|&nbsp;
                <strong>Modifié:</strong> {updatedAt ? new Date(updatedAt).toLocaleString('fr-BE') : '—'}
              </div>
            )}

            {error && (
              <div style={{
                gridColumn: '1 / -1', padding: 10,
                background: '#fee2e2', color: '#991b1b',
                border: '1px solid #fca5a5', borderRadius: 4, fontSize: 13,
              }}>{error}</div>
            )}
          </div>

          <div style={{
            padding: '12px 20px', borderTop: '1px solid #e2e8f0',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
          }}>
            <div>
              {isEdit && canDelete && !readOnly && (
                <button type="button" onClick={handleDelete} disabled={saving}
                        style={{
                          padding: '8px 14px', background: 'white', color: '#991b1b',
                          border: '1px solid #fca5a5', borderRadius: 4, fontSize: 13,
                          cursor: saving ? 'not-allowed' : 'pointer',
                        }}>
                  Supprimer
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={onClose} disabled={saving}
                      style={{
                        padding: '8px 16px', background: 'white', color: '#1e293b',
                        border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13,
                        cursor: saving ? 'not-allowed' : 'pointer',
                      }}>
                {readOnly ? 'Fermer' : 'Annuler'}
              </button>
              {!readOnly && (
                <button type="submit" disabled={saving}
                        style={{
                          padding: '8px 16px', background: '#2563eb', color: 'white',
                          border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 500,
                          cursor: saving ? 'not-allowed' : 'pointer',
                          opacity: saving ? 0.6 : 1,
                        }}>
                  {saving ? '…' : 'Enregistrer'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {quickCreateOpen && (
        <SupplyTypeQuickCreate
          onClose={() => setQuickCreateOpen(false)}
          onCreated={handleTypeCreated}
        />
      )}
    </>
  );
}

const backdropStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '60px 20px 20px', zIndex: 50, overflow: 'auto',
};
const formStyle = {
  background: 'white', borderRadius: 8, width: '100%', maxWidth: 580,
  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
};
const labelStyle = { display: 'flex', flexDirection: 'column' };
const labelTextStyle = {
  fontSize: 12, color: '#475569', marginBottom: 4, fontWeight: 500,
};
const inputStyle = {
  width: '100%', padding: 8, border: '1px solid #d1d5db',
  borderRadius: 4, fontSize: 13, boxSizing: 'border-box',
};
